<template>
  <div class="history-page page boss-page">
    <van-nav-bar title="日志历史" left-arrow @click-left="$router.back()"/>
    <section class="month">
      <button aria-label="上个月" @click="change(-1)">
        <van-icon name="arrow-left"/>
      </button>
      <div><b>{{ month.replace('-', ' 年 ') }} 月</b><span>{{ rows.length }} 篇日志</span></div>
      <button aria-label="下个月" @click="change(1)">
        <van-icon name="arrow"/>
      </button>
    </section>
    <van-skeleton v-if="loading" title :row="8"/>
    <main v-else>
      <article v-for="item in rows" :key="item.worklogId" @click="selected=item">
        <div class="date"><b>{{ item.workDate.slice(8) }}</b><span>{{ item.workDate.slice(5, 7) }}月</span></div>
        <div class="copy">
          <header><b>{{ item.cadence === 'MONTHLY' ? '周报' : '工作日报' }}</b><i
            :class="item.review ? item.review.action : 'WAIT'">{{ reviewText(item) }}</i></header>
          <span>{{ item.workDate }}</span>
          <p>{{ summary(item) }}</p></div>
        <van-icon class="arrow" name="arrow"/>
      </article>
      <van-empty v-if="!rows.length" description="本月暂无日志"/>
    </main>

    <van-popup v-model="show" position="bottom" round class="detail">
      <template v-if="selected">
        <div class="sheet-handle"/>
        <header class="detail-head">
          <div><span>{{ selected.cadence === 'MONTHLY' ? '周报' : '工作日报' }}</span>
            <h2>{{ selected.workDate }}</h2></div>
          <button aria-label="关闭" @click="selected=null">
            <van-icon name="cross"/>
          </button>
        </header>
        <section class="content-block">
          <h3>
            <van-icon name="records"/>
            日志内容
          </h3>
          <div v-for="(value,key) in selected.fieldValues" :key="key" class="field"><b>{{ fieldLabel(key) }}</b>
            <p>{{
                key === 'dailyLogs' ? formatDailyLogs(value) : (key === 'monthlyAchievements' ? formatWeeklyLogs(value) : (value || '未填写'))
              }}</p></div>
          <van-empty v-if="!Object.keys(selected.fieldValues || {}).length" description="暂无日志内容"/>
        </section>
        <section v-if="selected.review" class="review-block">
          <header>
            <h3>
              <van-icon name="certificate"/>
              主管复核
            </h3>
            <i :class="selected.review.action">{{ reviewText(selected) }}</i></header>
          <strong>{{ selected.review.finalScore }}<small>分</small></strong>
          <p>{{ selected.review.reason || '主管未填写复核说明' }}</p>
          <van-button v-if="selected.review.action === 'RETURN'" block color="#D32F2F" icon="edit" @click="revise(selected)">修改并重新提交
          </van-button>
        </section>
        <section v-if="selected.review && starRankOf(selected.review).length" class="star-block">
          <header>
            <h3>
              <van-icon name="star"/>
              五星上将排序
            </h3>
            <strong v-if="selected.review.starScore !== undefined && selected.review.starScore !== null">{{
                selected.review.starScore
              }}<small>分</small></strong></header>
          <ul class="star-ranks">
            <li v-for="(dim, i) in starRankOf(selected.review)" :key="i"><b>{{ i + 1 }}</b><span>{{ dim || '未选择' }}</span></li>
          </ul>
        </section>
        <section v-if="selected.reviewHistory && selected.reviewHistory.length" class="timeline">
          <h3>
            <van-icon name="clock-o"/>
            复核记录
          </h3>
          <div v-for="item in selected.reviewHistory" :key="item.id"><i/>
            <p><b>{{ reviewName(item.action) }} · {{ item.finalScore }} 分</b><span>{{ item.reason || '无补充说明' }}</span></p></div>
        </section>
      </template>
    </van-popup>
  </div>
</template>

<script>
/**
 * 员工日志历史：按月浏览已提交日志，弹层查看详情与主管复核结果。
 * 后端 MONTHLY cadence 在前端展示为「周报」。
 */
import coreapi from '@/utils/coreapi';
import {worklogFieldLabel} from '@/utils/worklogFields';

export default {
  data() {
    const d = new Date();
    return {month: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, rows: [], loading: false, selected: null};
  },
  computed: {
    show: {
      get() {
        return !!this.selected;
      }, set(value) {
        if (!value) this.selected = null;
      }
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        this.rows = await coreapi.history(this.month);
      } catch (e) {
        this.$toast.fail(e.message);
      } finally {
        this.loading = false;
      }
    },
    change(offset) {
      const parts = this.month.split('-');
      const d = new Date(+parts[0], +parts[1] - 1 + offset, 1);
      this.month = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      this.load();
    },
    summary(item) {
      const values = item && item.fieldValues ? item.fieldValues : {};
      const preferredCodes = item && item.cadence === 'MONTHLY'
        ? ['monthlyAchievements', 'nextMonthPlan', 'dailyWorkSummary', 'performanceWorkFacts']
        : ['dailyLogs', 'dailyWorkSummary', 'performanceWorkFacts', 'monthlyAchievements', 'nextMonthPlan'];

      for (const code of preferredCodes) {
        const text = this.summaryText(values[code], code);
        if (text) return this.truncateSummary(text);
      }

      // 量化日志没有正文时，使用中文字段名拼出可读摘要，避免把字段 JSON 直接展示出来。
      const quantitative = Object.keys(values).map(code => {
        const value = this.summaryText(values[code], code);
        if (!value || !this.isMeaningfulValue(value)) return '';
        return `${worklogFieldLabel(code)}：${value}`;
      }).filter(Boolean);
      if (quantitative.length) return this.truncateSummary(quantitative.join('；'));

      return '查看日志详情';
    },
    /**
     * 将字段值转换为列表可读的纯文本摘要。
     *
     * 日志正文通常以 JSON 字符串写入 field_values_json：
     * 日报取 today，周报取 thisWeek；其他结构递归提取文本值。
     * 这里只服务于列表展示，不修改后端原始数据，详情弹窗仍展示完整内容。
     */
    summaryText(value, code) {
      if (value === undefined || value === null) return '';
      if (typeof value === 'object') return this.extractSummaryText(value, code);

      const text = String(value).trim();
      if (!text) return '';
      const parsed = this.parseSummaryJson(text);
      return parsed === null ? this.cleanSummaryText(text) : this.extractSummaryText(parsed, code);
    },
    parseSummaryJson(value) {
      if (!/^[\[{]/.test(value)) return null;
      try {
        return JSON.parse(value);
      } catch (e) {
        return null;
      }
    },
    extractSummaryText(value, code) {
      if (value === undefined || value === null) return '';
      if (typeof value !== 'object') return this.cleanSummaryText(String(value));

      if (Array.isArray(value)) {
        return value.map(entry => this.extractSummaryText(entry, code)).filter(Boolean).join('；');
      }

      // 列表只展示当前周期的主要内容：日报取“今日”，周报取“本周”；
      // 主要内容为空时再回退到计划字段，避免把整段 JSON 展开到卡片里。
      const primaryKey = code === 'dailyLogs' ? 'today' : (code === 'monthlyAchievements' ? 'thisWeek' : '');
      const fallbackKey = code === 'dailyLogs' ? 'tomorrow' : (code === 'monthlyAchievements' ? 'nextWeek' : '');
      if (primaryKey && Object.prototype.hasOwnProperty.call(value, primaryKey)) {
        const primary = this.extractSummaryText(value[primaryKey], code);
        if (primary) return primary;
      }
      if (fallbackKey && Object.prototype.hasOwnProperty.call(value, fallbackKey)) {
        const fallback = this.extractSummaryText(value[fallbackKey], code);
        if (fallback) return fallback;
      }

      const keys = Object.keys(value).filter(key => key !== primaryKey && key !== fallbackKey);
      return keys.map(key => {
        const text = this.extractSummaryText(value[key], code);
        return text || '';
      }).filter(Boolean).join('；');
    },
    cleanSummaryText(value) {
      return String(value)
        .replace(/\s+/g, ' ')
        .replace(/^[\s"'{}\[\]]+|[\s"'{}\[\]]+$/g, '')
        .replace(/,\s*/g, '；')
        .trim();
    },
    isMeaningfulValue(value) {
      return value !== '0' && value !== '0.0' && value !== '0.00' && value !== '未填写';
    },
    truncateSummary(value) {
      const text = String(value).replace(/\s+/g, ' ').trim();
      return text.length > 100 ? `${text.slice(0, 100)}…` : text;
    },
    /** 字段 code → 中文标签，员工与主管详情共用同一份映射。 */
    fieldLabel(code) {
      return worklogFieldLabel(code);
    },
    formatDailyLogs(value) {
      if (!value) return '未填写';
      try {
        const parsed = JSON.parse(value);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          const today = (parsed.today || []).map((v, i) => '今日 ' + (i + 1) + '. ' + (v || '未填写')).join('\n');
          const tomorrow = (parsed.tomorrow || []).map((v, i) => '明日 ' + (i + 1) + '. ' + (v || '未填写')).join('\n');
          return [today, tomorrow].filter(Boolean).join('\n');
        }
        if (Array.isArray(parsed)) {
          return parsed.map((v, i) => (i + 1) + '. ' + (v || '未填写')).join('\n');
        }
      } catch (e) {
      }
      return value;
    },
    /** monthlyAchievements JSON → 本周/下周展示文案 */
    formatWeeklyLogs(value) {
      if (!value) return '未填写';
      try {
        const parsed = JSON.parse(value);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          const thisWeek = (parsed.thisWeek || []).map((v, i) => '本周 ' + (i + 1) + '. ' + (v || '未填写')).join('\n');
          const nextWeek = (parsed.nextWeek || []).map((v, i) => '下周 ' + (i + 1) + '. ' + (v || '未填写')).join('\n');
          return [thisWeek, nextWeek].filter(Boolean).join('\n');
        }
        if (Array.isArray(parsed)) {
          return parsed.map((v, i) => (i + 1) + '. ' + (v || '未填写')).join('\n');
        }
      } catch (e) {
      }
      return value;
    },
    reviewText(item) {
      return item.review ? this.reviewName(item.review.action) : '待复核';
    },
    reviewName(action) {
      return {APPROVE: '已通过', ADJUST: '已调整', RETURN: '已退回'}[action] || action;
    },
    /** 解析复核记录里的五星上将排序 JSON，缺省返回空数组 */
    starRankOf(review) {
      if (!review || !review.starRank) return [];
      try {
        const parsed = JSON.parse(review.starRank);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    },
    /** 退回后重新打开填写页：MONTHLY → 路由 WEEKLY */
    revise(item) {
      this.selected = null;
      this.$router.push({name: 'EfficiencyLog', query: {cadence: item.cadence === 'MONTHLY' ? 'WEEKLY' : item.cadence, date: item.workDate}});
    }
  }
};
</script>

<style lang="less" scoped>
@import "~@/assets/base.less";
@import '../boss/boss.less';

.history-page {
  min-height: calc(100vh - 1rem);
  padding-bottom: .4rem;
  background: #f6f7fb;
  color: #333;
  letter-spacing: 0
}

.history-page /deep/ .van-nav-bar {
  overflow: hidden;
  border-radius: .2rem .2rem 0 0;
  background-color: @color;
  background-image: linear-gradient(234deg, #D32F2F 0%, #1A1A1A 100%)
}

.history-page /deep/ .van-nav-bar__title, .history-page /deep/ .van-nav-bar .van-icon {
  color: #fff
}

.history-page /deep/ .van-nav-bar:after {
  border: 0
}

.month {
  height: 1.08rem;
  padding: 0 .3rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff
}

.month button {
  width: .72rem;
  height: .72rem;
  color: #73768a;
  font-size: .31rem
}

.month div {
  text-align: center
}

.month b, .month span {
  display: block
}

.month b {
  color: #34364a;
  font-size: .33rem
}

.month span {
  margin-top: .03rem;
  color: #9a9cac;
  font-size: .22rem
}

main {
  padding: .26rem .28rem
}

main article {
  min-height: 1.38rem;
  margin-bottom: .16rem;
  padding: .2rem .22rem;
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: .2rem;
  box-shadow: 0 .06rem .24rem rgba(211, 47, 47, .08);
  box-sizing: border-box
}

.date {
  width: .82rem;
  height: .82rem;
  display: flex;
  flex: 0 0 .82rem;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: @color;
  border-radius: .18rem;
  background: #fff5f5
}

.date b {
  font-size: .38rem;
  line-height: 1
}

.date span {
  margin-top: .05rem;
  font-size: .2rem
}

.copy {
  min-width: 0;
  margin-left: .2rem;
  flex: 1
}

.copy header {
  display: flex;
  align-items: center
}

.copy header b {
  min-width: 0;
  flex: 1;
  color: #34364a;
  font-size: .31rem
}

.copy header i {
  padding: .06rem .12rem;
  border-radius: .2rem;
  font-size: .21rem;
  font-style: normal
}

.copy > span {
  display: block;
  margin-top: .04rem;
  color: #8f92a3;
  font-size: .23rem
}

.copy p {
  margin-top: .07rem;
  overflow: hidden;
  color: #777b8e;
  font-size: .25rem;
  text-overflow: ellipsis;
  white-space: nowrap
}

.arrow {
  margin-left: .12rem;
  color: #b4b6c3;
  font-size: .26rem
}

.WAIT {
  color: #a36b23;
  background: #fff3df
}

.APPROVE {
  color: #268653;
  background: #e8f7ef
}

.ADJUST {
  color: @color;
  background: #eff0ff
}

.RETURN {
  color: #c33;
  background: #fee
}

.detail {
  width: 100%;
  max-height: 82vh;
  padding: 0 .32rem .4rem;
  overflow-y: auto;
  box-sizing: border-box
}

.sheet-handle {
  width: .72rem;
  height: .08rem;
  margin: .16rem auto .2rem;
  border-radius: .08rem;
  background: #d9dae2
}

.detail-head {
  display: flex;
  align-items: center;
  justify-content: space-between
}

.detail-head span {
  color: @color;
  font-size: .24rem
}

.detail-head h2 {
  margin-top: .05rem;
  color: #34364a;
  font-size: .4rem
}

.detail-head button {
  width: .64rem;
  height: .64rem;
  color: #777b8e;
  font-size: .34rem;
  border-radius: 50%;
  background: #f4f4f7
}

.content-block, .review-block, .timeline {
  margin-top: .25rem;
  padding: .27rem;
  background: #f8f8fc;
  border-radius: .18rem
}

.detail h3 {
  display: flex;
  align-items: center;
  color: #34364a;
  font-size: .31rem
}

.detail h3 .van-icon {
  margin-right: .12rem;
  color: @color;
  font-size: .32rem
}

.field {
  padding: .2rem 0;
  border-bottom: 1PX solid #e8e8ee
}

.field:last-child {
  border-bottom: 0
}

.field b {
  display: block;
  color: #8b8e9e;
  font-size: .23rem
}

.field p {
  margin-top: .08rem;
  color: #3f4252;
  font-size: .29rem;
  line-height: 1.55;
  white-space: pre-wrap
}

.review-block > header {
  display: flex;
  align-items: center;
  justify-content: space-between
}

.review-block > header i {
  padding: .07rem .13rem;
  border-radius: .2rem;
  font-size: .22rem;
  font-style: normal
}

.review-block > strong {
  display: block;
  margin-top: .2rem;
  color: @color;
  font-size: .5rem
}

.review-block > strong small {
  margin-left: .05rem;
  font-size: .24rem
}

.review-block > p {
  margin-top: .08rem;
  color: #626577;
  font-size: .26rem;
  line-height: 1.5
}

.review-block .van-button {
  height: .78rem;
  margin-top: .22rem;
  border-radius: .14rem
}

.star-block {
  margin-top: .25rem;
  padding: .27rem;
  background: #f8f8fc;
  border-radius: .18rem
}

.star-block header {
  display: flex;
  align-items: center;
  justify-content: space-between
}

.star-block header strong {
  color: @color;
  font-size: .5rem
}

.star-block header strong small {
  margin-left: .05rem;
  font-size: .24rem
}

.star-ranks {
  margin-top: .2rem;
  display: flex;
  flex-wrap: wrap;
  gap: .14rem
}

.star-ranks li {
  display: flex;
  align-items: center;
  padding: .12rem .1rem;
  background: #fff;
  border: 1px solid #eee;
  border-radius: .14rem
}

.star-ranks li b {
  width: .4rem;
  height: .4rem;
  line-height: .4rem;
  text-align: center;
  border-radius: 50%;
  background: #fff5f5;
  color: @color;
  font-size: .24rem;
  margin-right: .12rem
}

.star-ranks li span {
  color: #3f4252;
  font-size: .26rem
}

.timeline > div {
  position: relative;
  padding: .2rem 0 .04rem .32rem
}

.timeline > div > i {
  position: absolute;
  left: .03rem;
  top: .29rem;
  width: .13rem;
  height: .13rem;
  border: .05rem solid #dfe0fb;
  border-radius: 50%;
  background: @color
}

.timeline > div:after {
  content: "";
  position: absolute;
  left: .105rem;
  top: .47rem;
  bottom: -.2rem;
  width: 1PX;
  background: #dddff0
}

.timeline > div:last-child:after {
  display: none
}

.timeline p b, .timeline p span {
  display: block
}

.timeline p b {
  color: #4a4d5f;
  font-size: .26rem
}

.timeline p span {
  margin-top: .06rem;
  color: #858898;
  font-size: .24rem;
  line-height: 1.45
}
.history-page { margin: 0; min-height: 100vh; background: #f6f7fb; padding-bottom: 1.7rem; }
.history-page /deep/ .van-nav-bar { margin: 0; width: 100%; height: 1.22667rem; border-radius: 0; background: linear-gradient(135deg,#351b1d,#c6282d); }
</style>



// WEBPACK FOOTER //
// src/views/efficiency/History.vue